<script setup lang="ts">
import { reactive, ref, useAuth, useRouter, useToast } from "#imports";
import MPInput from "~/components/forms/MPInput.vue";
import MPButton from "~/components/forms/MPButton.vue";
import MPMessage from "~/components/widgets/MPMessage.vue";
import {
  isMaxStringLength,
  isMinStringLength,
  isRequired,
} from "~/components/forms/validation";

const { showError, showSuccess } = useToast();
const authStore = useAuth();
const router = useRouter();

const failed = ref(false);
const formSubmitted = ref(false);
const formData = reactive({
  password: null as null | string,
  confirmPassword: null as null | string,
});

const resetPassword = async () => {
  try {
    formSubmitted.value = true;

    await authStore.resetPassword({
      password: formData.password!,
    });

    showSuccess("Password has been successfully updated");
    router.push("/");
  } catch (error) {
    console.error("Error updating password:", error);

    let errorMessage = "An unknown error occurred";
    if (error instanceof Error) {
      errorMessage = error.message;
    }
    showError(errorMessage);
    formSubmitted.value = false;
    failed.value = true;
  }
};
</script>

<template>
  <div class="container">
    <main>
      <div class="login-container">
        <h4>Set new password</h4>
        <p>Create a new password for your account</p>
        <form class="form-container" @submit.prevent="resetPassword">
          <MPInput
            id="password"
            v-model="formData.password"
            type="password"
            name="password"
            :placeholder="$t('auth.forgot.newPassword')"
            :validate="
              (v) =>
                isRequired(v) ||
                isMinStringLength(8)(v) ||
                isMaxStringLength(255)(v)
            "
          />
          <MPInput
            id="confirmPassword"
            v-model="formData.confirmPassword"
            :placeholder="$t('auth.forgot.confirmNewPassword')"
            type="password"
            name="confirmPassword"
            :validate="
              (v) =>
                isRequired(v) ||
                (formData.password !== v ? 'Passwords must match' : undefined)
            "
          />
          <MPButton
            v-if="!formSubmitted"
            :label="$t('auth.forgot.reset')"
            type="submit"
          />
          <div v-if="formSubmitted" class="reset-password-success">Loading</div>
          <div v-if="failed" class="reset-password-failed">
            <MPMessage version="error">
              The password reset link is invalid or has expired. Please go back
              to the login page and
              <NuxtLinkLocale to="/reset-password">
                request a new password reset link.
              </NuxtLinkLocale>
            </MPMessage>
          </div>
        </form>
      </div>
    </main>
  </div>
</template>
<style scoped>
.login-container {
  width: 20rem;
  text-align: center;
  position: absolute;
  display: grid;
  gap: 1rem;
  top: 25%;
  left: 50%;
  transform: translateY(-50%);
  transform: translateX(-50%);
}

.login-container form {
  display: grid;
  gap: 1rem;
}

p {
  color: var(--color-slate-400);
}

a {
  color: var(--color-indigo-500);
}
</style>
